/**
 * **Zählerdaten** — Laufzeitdaten-Dashboard eines Gebäudes je Medium und Knoten der Zählerhierarchie
 * (tri-server `metering/dashboard`).
 *
 * Kennzahlen (Verbrauch mit Vorperiode, Spitze, Grundlast, nicht erfasst, spezifisch, Vollständigkeit),
 * Lastgang gestapelt nach Unterzählern plus Rest, Heatmap Tag × Uhrzeit, Energiesignatur gegen die
 * Außentemperatur, typischer Werktag/Samstag/Sonntag, Rangliste der Verbraucher. Ein Klick in der Rangliste
 * auf einen Zähler mit Unterzählern geht eine Ebene tiefer.
 *
 * CO₂: Kennzahlen markt- und standortbasiert; Lastgang und Energiefluss lassen sich auf kg CO₂(e) umschalten
 * (Faktoren je Reihe bzw. Knoten vom Server, aus Liefervertrag bzw. UBA/BAFA).
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactApexChart from "react-apexcharts";
import { ArrowDownRight, ArrowUpRight, ChevronRight, Loader2 } from "lucide-react";
import { useModalContext } from "../../components/ModalProvider";
import { Co2Factors, MEDIA, Medium, MeterDashboard, useMeteringAPI } from "../../MeteringAPI";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import MeterSankey from "./MeterSankey";

const ALL = "__building__";
const COLORS = ["#5F3A79", "#98AC4D", "#001A61", "#E07A2F", "#2A9D8F", "#B5838D", "#6D597A", "#E9C46A", "#264653", "#8AB17D", "#9C6644"];
const REST_COLOR = "#B0B0B8";
/**
 * Farbe mit Weiß gemischt — wirkt wie 60 % Transparenz auf hellem Grund. Bei gestapelten Flächen reicht jede
 * Fläche bis zur Nulllinie; echte Transparenz addiert sich dort und wird unten fast deckend dunkel.
 */
const tint = (hex: string, alpha = 0.4) => {
  const n = parseInt(hex.slice(1), 16);
  const mix = (c: number) => Math.round(c * alpha + 255 * (1 - alpha));
  return `rgb(${mix((n >> 16) & 255)}, ${mix((n >> 8) & 255)}, ${mix(n & 255)})`;
};
const PRESETS = ["24h", "7d", "30d", "ytd", "12m"] as const;
type Preset = (typeof PRESETS)[number] | "custom";

const nf = (v: number | null | undefined, digits = 1) => (v == null ? "—" : new Intl.NumberFormat("de-DE", { maximumFractionDigits: digits }).format(v));
const big = (v: number | null | undefined, unit: string) => {
  if (v == null) return "—";
  if (unit === "kWh" && Math.abs(v) >= 10_000) return `${nf(v / 1000, 1)} MWh`;
  return `${nf(v, v >= 100 ? 0 : 1)} ${unit}`;
};
const rangeFor = (preset: Preset, custom: { from: string; to: string }) => {
  const now = new Date();
  if (preset === "custom" && custom.from && custom.to) return { from: new Date(`${custom.from}T00:00:00`), to: new Date(`${custom.to}T23:59:59`) };
  if (preset === "24h") return { from: new Date(now.getTime() - 86_400_000), to: now };
  if (preset === "7d") return { from: new Date(now.getTime() - 7 * 86_400_000), to: now };
  if (preset === "ytd") return { from: new Date(now.getFullYear(), 0, 1), to: now };
  if (preset === "12m") return { from: new Date(now.getFullYear() - 1, now.getMonth(), now.getDate()), to: now };
  return { from: new Date(now.getTime() - 30 * 86_400_000), to: now };
};

type Basis = "energy" | "market" | "location";
const CO2_UNIT = "kg CO₂e";
/** Menge in der Einheit des Mediums × g je Einheit → kg; ohne Faktor 0 (eigene Erzeugung, kein Faktor). */
const toKg = (value: number, f: Co2Factors | undefined, basis: Basis) => {
  if (basis === "energy") return value;
  const g = f?.[basis];
  return g == null ? 0 : (value * g) / 1000;
};

function Kpi({ title, value, hint, tone }: { title: string; value: string; hint?: React.ReactNode; tone?: "warn" }) {
  return (
    <div className="min-w-0 rounded-md border p-3" data-testid="meter-kpi">
      <div className="truncate text-xs text-muted-foreground" title={title}>
        {title}
      </div>
      <div className={`mt-1 truncate text-xl font-semibold tabular-nums ${tone === "warn" ? "text-amber-700" : ""}`}>{value}</div>
      {hint ? <div className="mt-0.5 truncate text-xs text-muted-foreground">{hint}</div> : null}
    </div>
  );
}

export default function MeterDataDashboard({ building }: { building: any }) {
  const { t } = useTranslation();
  const api = useMeteringAPI();
  const { pushError } = useModalContext() as any;
  const buildingId = String(building?._id ?? "");
  const [media, setMedia] = useState<Medium[] | null>(null);
  const [medium, setMedium] = useState<Medium | null>(null);
  const [node, setNode] = useState<string | null>(null);
  const [preset, setPreset] = useState<Preset>("30d");
  const [custom, setCustom] = useState({ from: "", to: "" });
  const [data, setData] = useState<MeterDashboard | null>(null);
  const [loading, setLoading] = useState(false);
  const [basis, setBasis] = useState<Basis>("energy");

  // Welche Medien es im Gebäude gibt (aus dem Zählerbaum), Vorauswahl das mit der größten Hierarchie.
  useEffect(() => {
    if (!buildingId) return;
    api
      .meters(buildingId)
      .then(tree => {
        const present = MEDIA.filter(m => (tree.media[m] ?? []).length);
        setMedia(present);
        const linked = present
          .map(m => ({ m, n: (tree.media[m] ?? []).filter(r => r.children.length).length }))
          .sort((a, b) => b.n - a.n);
        setMedium(linked[0]?.n ? linked[0].m : present[0] ?? null);
      })
      .catch((error: any) => pushError(error));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildingId]);

  const range = useMemo(() => rangeFor(preset, custom), [preset, custom]);

  useEffect(() => {
    if (!medium) return;
    let cancelled = false;
    setLoading(true);
    api
      .dashboard(buildingId, { medium, node, ...range })
      .then(result => !cancelled && setData(result))
      .catch((error: any) => !cancelled && pushError(error))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildingId, medium, node, range]);

  if (!media) return <Skeleton className="h-64 w-full" />;
  if (!media.length) return <p className="text-sm text-muted-foreground">{t("metering.empty")}</p>;

  const unit = data?.unit ?? "";
  const perBucket = data ? t(`meterData.per.${data.resolution}`) : "";
  const k = data?.kpis;
  const hasCo2 = k?.co2Market != null || k?.co2Location != null;
  const shownBasis: Basis = hasCo2 ? basis : "energy";
  const shownUnit = shownBasis === "energy" ? unit : CO2_UNIT;

  return (
    <div className="flex min-w-0 flex-col gap-4" data-testid="meter-data-dashboard">
      <div className="flex flex-wrap items-center gap-2">
        {media.map(m => (
          <Button key={m} size="sm" variant={m === medium ? "default" : "outline"} onClick={() => {
              setMedium(m);
              setNode(null);
            }}>
            {t(`metering.medium.${m}`)}
          </Button>
        ))}
        <span className="mx-1 h-6 w-px bg-border" />
        <Select value={node ?? ALL} onValueChange={v => setNode(v === ALL ? null : v)}>
          <SelectTrigger className="h-9 w-72 max-w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t("meterData.wholeBuilding")}</SelectItem>
            {(data?.nodes ?? []).map(n => (
              <SelectItem key={n.id} value={n.id}>
                {`${"  ".repeat(n.depth)}${n.depth ? "└ " : ""}${n.name} (${n.children})`}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="mx-1 h-6 w-px bg-border" />
        {PRESETS.map(p => (
          <Button key={p} size="sm" variant={p === preset ? "default" : "ghost"} onClick={() => setPreset(p)}>
            {t(`meterData.preset.${p}`)}
          </Button>
        ))}
        <Input type="date" className="h-9 w-36" value={custom.from} onChange={e => {
            setCustom({ ...custom, from: e.target.value });
            setPreset("custom");
          }} />
        <Input type="date" className="h-9 w-36" value={custom.to} onChange={e => {
            setCustom({ ...custom, to: e.target.value });
            setPreset("custom");
          }} />
        {hasCo2 ? (
          <>
            <span className="mx-1 h-6 w-px bg-border" />
            {(["energy", "market", "location"] as const).map(b => (
              <Button key={b} size="sm" variant={b === shownBasis ? "secondary" : "ghost"} onClick={() => setBasis(b)} title={t(`meterData.basisHint.${b}`)}>
                {t(`meterData.basis.${b}`)}
              </Button>
            ))}
          </>
        ) : null}
        {loading ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /> : null}
      </div>

      {data?.node ? (
        <div className="flex flex-wrap items-center gap-1 text-sm">
          <button type="button" className="text-primary underline" onClick={() => setNode(null)}>
            {t("meterData.wholeBuilding")}
          </button>
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">{data.node.name}</span>
        </div>
      ) : null}

      {!data ? (
        <Skeleton className="h-96 w-full" />
      ) : (
        <>
          <div className="grid min-w-0 grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
            <Kpi
              title={t("meterData.kpi.consumption")}
              value={big(k!.consumption, unit)}
              hint={
                k!.change != null ? (
                  <span className={`inline-flex items-center gap-0.5 ${k!.change > 0 ? "text-red-600" : "text-emerald-700"}`}>
                    {k!.change > 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                    {nf(k!.change)} % {t("meterData.vsPrevious")}
                  </span>
                ) : (
                  t("meterData.noPrevious")
                )
              }
            />
            <Kpi
              title={t("meterData.kpi.peak")}
              value={`${nf(k!.peak15 ?? k!.peakHour, 0)} ${unit === "kWh" ? "kW" : `${unit}/h`}`}
              hint={k!.peakAt ? new Date(k!.peakAt).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" }) : undefined}
            />
            <Kpi title={t("meterData.kpi.base")} value={`${nf(k!.baseLoad, 0)} ${unit === "kWh" ? "kW" : `${unit}/h`}`} hint={k!.baseShare != null ? t("meterData.ofPeak", { share: nf(k!.baseShare) }) : undefined} />
            <Kpi
              title={t("meterData.kpi.unmetered")}
              value={k!.unmetered != null ? `${nf(k!.unmetered)} %` : "—"}
              hint={k!.unmetered == null ? t("meterData.chooseNode") : t("meterData.unmeteredHint")}
              tone={k!.unmetered != null && (k!.unmetered < 0 || k!.unmetered > 25) ? "warn" : undefined}
            />
            <Kpi title={t("meterData.kpi.specific")} value={k!.specific != null ? `${nf(k!.specific)} ${unit}/m²` : "—"} hint={k!.area ? `${nf(k!.area, 0)} m²` : t("meterData.noArea")} />
            <Kpi
              title={t("meterData.kpi.completeness")}
              value={k!.completeness != null ? `${nf(k!.completeness)} %` : "—"}
              hint={data.withoutData ? t("meterData.withoutData", { count: data.withoutData }) : undefined}
              tone={k!.completeness != null && k!.completeness < 95 ? "warn" : undefined}
            />
            {hasCo2 ? (
              <>
                <Kpi
                  title={t("meterData.kpi.co2Market")}
                  value={k!.co2Market != null ? `${nf(k!.co2Market)} t` : "—"}
                  hint={<span title={k!.co2Sources.join(" · ")}>{t(k!.co2Notes.includes("contract") ? "meterData.co2FromContract" : "meterData.co2Fallback")}</span>}
                  tone={k!.co2Notes.includes("contract") ? undefined : "warn"}
                />
                <Kpi title={t("meterData.kpi.co2Location")} value={k!.co2Location != null ? `${nf(k!.co2Location)} t` : "—"} hint={<span title={k!.co2Sources.join(" · ")}>{t("meterData.co2LocationHint", { year: k!.co2Year })}</span>} />
                <Kpi
                  title={t("meterData.kpi.cost")}
                  value={k!.cost != null ? `${nf(k!.cost, 0)} ${k!.costCurrency === "EUR" || !k!.costCurrency ? "€" : k!.costCurrency}` : "—"}
                  hint={k!.cost != null ? t("meterData.costHint", { energy: nf(k!.costEnergy, 0), base: nf(k!.costBase, 0) }) : t("meterData.costNone")}
                />
                <Kpi title={t("meterData.kpi.co2Intensity")} value={k!.co2Intensity != null ? `${nf(k!.co2Intensity)} kg/m²` : "—"} hint={k!.area ? `${nf(k!.area, 0)} m²` : t("meterData.noArea")} />
              </>
            ) : null}
          </div>
          {k!.co2Notes.some(n => n === "ownGeneration" || n === "gasUnit") ? (
            <p className="-mt-2 text-xs text-muted-foreground">
              {k!.co2Notes.includes("ownGeneration") ? t("meterData.co2OwnGeneration") : null} {k!.co2Notes.includes("gasUnit") ? t("meterData.co2GasUnit") : null}
            </p>
          ) : null}

          <section className="min-w-0 rounded-md border p-3">
            <h3 className="mb-1 text-sm font-semibold">
              {t("meterData.loadProfile")} <span className="font-normal text-muted-foreground">({shownUnit} {perBucket})</span>
            </h3>
            <LoadProfile data={data} basis={shownBasis} />
          </section>

          <FlowSection data={data} basis={shownBasis} />

          <div className="grid min-w-0 gap-4 xl:grid-cols-2">
            <section className="min-w-0 rounded-md border p-3">
              <h3 className="mb-1 text-sm font-semibold">
                {t("meterData.heatmap")} <span className="font-normal text-muted-foreground">({unit} {t("meterData.per.hour")})</span>
              </h3>
              <Heatmap data={data} />
            </section>
            <section className="min-w-0 rounded-md border p-3">
              <h3 className="mb-1 text-sm font-semibold">{t("meterData.signature")}</h3>
              <SignatureChart data={data} />
            </section>
            <section className="min-w-0 rounded-md border p-3">
              <h3 className="mb-1 text-sm font-semibold">
                {t("meterData.typicalDay")} <span className="font-normal text-muted-foreground">({unit === "kWh" ? "kW" : `${unit}/h`})</span>
              </h3>
              <TypicalDay data={data} />
            </section>
            <section className="min-w-0 rounded-md border p-3">
              <h3 className="mb-1 text-sm font-semibold">{t("meterData.ranking")}</h3>
              <Ranking data={data} onDrill={id => setNode(id)} />
            </section>
          </div>
        </>
      )}
    </div>
  );
}

function LoadProfile({ data, basis }: { data: MeterDashboard; basis: Basis }) {
  const colors = data.series.map((s, i) => (s.id === "rest" ? REST_COLOR : COLORS[i % COLORS.length]));
  const unit = basis === "energy" ? data.unit : CO2_UNIT;
  return (
    <ReactApexChart
      type="area"
      height={320}
      series={data.series.map(s => ({ name: s.name, data: s.data.map(([x, v]) => [x, toKg(v, data.seriesFactors?.[s.id], basis)] as [number, number]) }))}
      options={{
        chart: { stacked: true, toolbar: { show: true }, zoom: { enabled: true }, animations: { enabled: false } },
        colors,
        dataLabels: { enabled: false },
        stroke: { width: 1, colors, curve: data.resolution === "day" ? "stepline" : "smooth" },
        fill: { type: "solid", opacity: 1, colors: colors.map(c => tint(c, 0.2)) },
        xaxis: { type: "datetime", labels: { datetimeUTC: false } },
        yaxis: { labels: { formatter: (v: number) => nf(v, 0) } },
        legend: { position: "bottom", fontSize: "11px" },
        tooltip: { x: { format: data.resolution === "day" ? "dd.MM.yyyy" : "dd.MM.yyyy HH:mm" }, y: { formatter: (v: number) => `${nf(v)} ${unit}` } },
      }}
    />
  );
}

function Heatmap({ data }: { data: MeterDashboard }) {
  const { t } = useTranslation();
  const values = data.heatmap.flatMap(d => d.hours.filter((v): v is number => v != null));
  const max = values.length ? Math.max(...values) : 1;
  const series = Array.from({ length: 24 }, (_, h) => 23 - h).map(h => ({
    name: `${String(h).padStart(2, "0")}:00`,
    data: data.heatmap.map(d => ({ x: d.day, y: d.hours[h] ?? 0 })),
  }));
  if (!data.heatmap.length) return <p className="text-sm text-muted-foreground">{t("meterData.noData")}</p>;
  return (
    <ReactApexChart
      type="heatmap"
      height={360}
      series={series}
      options={{
        chart: { toolbar: { show: false }, animations: { enabled: false } },
        dataLabels: { enabled: false },
        stroke: { width: 0 },
        plotOptions: {
          heatmap: {
            radius: 0,
            enableShades: false,
            colorScale: {
              ranges: [0, 0.15, 0.3, 0.45, 0.6, 0.75, 0.9].map((p, i, a) => ({
                from: p * max,
                to: (a[i + 1] ?? 1.01) * max,
                color: ["#EEF2E3", "#D6E2B5", "#B9CC80", "#98AC4D", "#7A6A8F", "#5F3A79", "#3D2150"][i],
              })),
            },
          },
        },
        xaxis: { type: "category", labels: { show: data.heatmap.length <= 45, rotate: -45, style: { fontSize: "9px" } }, tickAmount: 12 },
        yaxis: { labels: { style: { fontSize: "9px" } } },
        legend: { show: false },
        tooltip: { y: { formatter: (v: number) => `${nf(v)} ${data.unit}` } },
      }}
    />
  );
}

function SignatureChart({ data }: { data: MeterDashboard }) {
  const { t } = useTranslation();
  if (!data.signaturePoints.length) return <p className="text-sm text-muted-foreground">{t("meterData.noWeather")}</p>;
  const by = (type: string) => data.signaturePoints.filter(p => p.dayType === type).map(p => [p.t, p.value] as [number, number]);
  const sig = data.signature;
  const series: any[] = [
    { name: t("meterData.dayType.workday"), type: "scatter", data: by("workday") },
    { name: t("meterData.dayType.saturday"), type: "scatter", data: by("saturday") },
    { name: t("meterData.dayType.sunday"), type: "scatter", data: by("sunday") },
  ];
  if (sig) series.push({ name: t("meterData.fit"), type: "line", data: sig.line.map(p => [p.t, p.value]) });
  return (
    <>
      <ReactApexChart
        type="line"
        height={300}
        series={series}
        options={{
          chart: { toolbar: { show: false }, zoom: { enabled: false }, animations: { enabled: false } },
          colors: ["#5F3A79", "#98AC4D", "#E07A2F", "#001A61"],
          markers: { size: [3, 3, 3, 0] },
          stroke: { width: [0, 0, 0, 2] },
          xaxis: { type: "numeric", title: { text: t("meterData.outsideTemp") }, labels: { formatter: (v: any) => `${Math.round(Number(v))} °C` } },
          yaxis: { title: { text: `${data.unit} / ${t("meterData.dayUnit")}` }, labels: { formatter: (v: number) => nf(v, 0) } },
          legend: { position: "bottom", fontSize: "11px" },
          tooltip: { shared: false, x: { formatter: (v: number) => `${nf(v)} °C` }, y: { formatter: (v: number) => `${nf(v, 0)} ${data.unit}` } },
        }}
      />
      {sig ? (
        <p className="mt-1 text-xs text-muted-foreground" data-testid="meter-signature">
          {t(`meterData.signatureText.${sig.model}`, { base: nf(sig.base, 0), slope: nf(sig.slope, 0), balance: nf(sig.balance), unit: data.unit, r2: nf(sig.r2, 2) })}
        </p>
      ) : null}
    </>
  );
}

function TypicalDay({ data }: { data: MeterDashboard }) {
  const { t } = useTranslation();
  const hours = Array.from({ length: 24 }, (_, h) => h);
  return (
    <ReactApexChart
      type="line"
      height={280}
      series={(["workday", "saturday", "sunday"] as const).map(k => ({ name: t(`meterData.dayType.${k}`), data: hours.map(h => data.typicalDay[k][h] ?? null) }))}
      options={{
        chart: { toolbar: { show: false }, zoom: { enabled: false }, animations: { enabled: false } },
        colors: ["#5F3A79", "#98AC4D", "#E07A2F"],
        stroke: { width: 2, curve: "smooth" },
        xaxis: { categories: hours.map(h => `${String(h).padStart(2, "0")}`), title: { text: t("meterData.hourOfDay") } },
        yaxis: { labels: { formatter: (v: number) => nf(v, 0) } },
        legend: { position: "bottom", fontSize: "11px" },
        tooltip: { y: { formatter: (v: number) => `${nf(v)} ${data.unit === "kWh" ? "kW" : `${data.unit}/h`}` } },
      }}
    />
  );
}

function Ranking({ data, onDrill }: { data: MeterDashboard; onDrill: (id: string) => void }) {
  const { t } = useTranslation();
  const max = Math.max(1, ...data.ranking.map(r => r.consumption ?? 0));
  if (!data.ranking.length) return <p className="text-sm text-muted-foreground">{t("meterData.noData")}</p>;
  return (
    <div className="max-h-[300px] overflow-y-auto">
      <table className="w-full text-xs">
        <thead className="sticky top-0 bg-background text-left text-muted-foreground">
          <tr>
            <th className="py-1 font-medium">{t("meterData.meter")}</th>
            <th className="w-1/3 py-1 font-medium">{t("meterData.share")}</th>
            <th className="py-1 text-right font-medium">{t("meterData.trend")}</th>
          </tr>
        </thead>
        <tbody>
          {data.ranking.map(r => (
            <tr key={r.id} className="border-t">
              <td className="max-w-0 py-1 pr-2">
                {r.hasChildren ? (
                  <button type="button" className="block w-full truncate text-left text-primary underline" title={r.name} onClick={() => onDrill(r.id)}>
                    {r.name}
                  </button>
                ) : (
                  <span className="block truncate" title={r.name}>
                    {r.name}
                  </span>
                )}
                <span className="flex flex-wrap gap-1">
                  {r.flags.map(f => (
                    <Badge key={f} variant="secondary" className="px-1 py-0 text-[10px]">
                      {t(`meterData.flag.${f}`)}
                    </Badge>
                  ))}
                </span>
              </td>
              <td className="py-1">
                <div className="flex items-center gap-2">
                  <div className="h-2 flex-1 rounded bg-muted">
                    <div className="h-2 rounded bg-primary" style={{ width: `${Math.max(1, ((r.consumption ?? 0) / max) * 100)}%` }} />
                  </div>
                  <span className="w-20 shrink-0 text-right tabular-nums">{big(r.consumption, data.unit)}</span>
                </div>
              </td>
              <td className={`py-1 text-right tabular-nums ${r.trend != null && r.trend > 0 ? "text-red-600" : "text-emerald-700"}`}>{r.trend != null ? `${r.trend > 0 ? "+" : ""}${nf(r.trend)} %` : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Fluss in kg CO₂(e): Faktor des Zielknotens, sonst der Quelle; Verbindungen ohne Emissionen fallen weg. */
const flowIn = (flow: MeterDashboard["flow"]["energy"], factors: MeterDashboard["flowFactors"], basis: Basis) => {
  if (basis === "energy") return flow;
  const links = flow.links
    .map(l => ({ ...l, value: toKg(l.value, factors?.[l.target]?.[basis] != null ? factors[l.target] : factors?.[l.source], basis) }))
    .filter(l => l.value > 0);
  const used = new Set(links.flatMap(l => [l.source, l.target]));
  return { nodes: flow.nodes.filter(n => used.has(n.id)), links };
};

function FlowSection({ data, basis }: { data: MeterDashboard; basis: Basis }) {
  const { t } = useTranslation();
  const [view, setView] = useState<"energy" | "tenants">("energy");
  const raw = data.flow?.[view];
  const flow = raw ? flowIn(raw, data.flowFactors, basis) : null;
  const unit = basis === "energy" ? data.unit : CO2_UNIT;
  return (
    <section className="min-w-0 rounded-md border p-3">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold">
          {t("meterData.flow")} <span className="font-normal text-muted-foreground">({unit})</span>
        </h3>
        <div className="ml-auto flex gap-1">
          <Button size="sm" variant={view === "energy" ? "default" : "outline"} onClick={() => setView("energy")}>
            {t("meterData.flowEnergy")}
          </Button>
          <Button size="sm" variant={view === "tenants" ? "default" : "outline"} onClick={() => setView("tenants")}>
            {t("meterData.flowTenants")}
          </Button>
        </div>
      </div>
      {view === "tenants" && !data.flow?.tenants.assignedMeters ? (
        <p className="mb-2 text-xs text-muted-foreground">{t("meterData.flowTenantsHint")}</p>
      ) : null}
      {flow ? <MeterSankey flow={flow} unit={unit} /> : null}
    </section>
  );
}
